<template>
    <div class="CCC__table">
        <img v-if="stockData.length" class="logo" src="../assets/earthpositive-block.svg" alt="">

        <div v-if="errorMsg">{{ errorMsg }}</div>
        <div v-else-if="stockData.length">
            <div class="table__inputs">
                <input v-model="globalFilter" @input="applyGlobalFilter" placeholder="Search all columns..." />
                <div>
                    <button :disabled="(table.getRowModel().rows.length == 0) ? true : false" @click.prevent="downloadFilteredData">Download Search Results (CSV)</button>
                    <button @click.prevent="downloadAllData">Download All (CSV)</button>
                </div>
            </div>
            <table v-if="table.getRowModel().rows.length != 0">
                <thead>
                    <tr>
                        <th v-for="column in table.getHeaderGroups()[0].headers" :key="column.id" @click="handleSort(column)">
                            <span>
                                <span>{{ column.column.columnDef.header }}</span>
                                <span class="sorting__arrows" v-if="column.column.getIsSorted()">
                                    <span v-if="column.column.getIsSorted() === 'asc'">↑</span>
                                    <span v-if="column.column.getIsSorted() === 'desc'">↓</span>
                                </span>
                                <span class="sorting__arrows" v-else>
                                    <span>↑</span>
                                    <span>↓</span>
                                </span>
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in table.getRowModel().rows" :key="row.id">
                        <td v-for="cell in row.getVisibleCells()" :key="cell.id">
                            {{ cell.getValue() }}
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-else>
                <h1>No Results.</h1>
            </div>

            <div class="table__footer" v-if="table.getRowModel().rows.length != 0">
                <div class="pagination">
                    <button @click="table.firstPage()" :disabled="!table.getCanPreviousPage()">
                        1
                    </button>
                    <button @click="table.previousPage()" :disabled="!table.getCanPreviousPage()">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                            <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
                        </svg>
                    </button>
                    <span>
                        Page {{ table.getState().pagination.pageIndex + 1 }} of {{ totalPages }}
                    </span>
                    <button @click="table.nextPage()" :disabled="!table.getCanNextPage()">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                            <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
                        </svg>
                    </button>
                    <button @click="table.lastPage()" :disabled="!table.getCanNextPage()">
                        {{ table.getPageCount() }}
                    </button>
                </div>

                <select class="page__size" v-model="pageSize">
                    <option :value="20">20 Per Page</option>
                    <option :value="50">50 Per Page</option>
                    <option :value="100">100 Per Page</option>
                </select>
            </div>
        </div>
        <div v-else>
            <svg class="spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path class="fa-secondary" opacity=".4" d="M0 256C0 397.4 114.6 512 256 512c94.7 0 177.5-51.5 221.7-128l-55.4-32c-33.2 57.4-95.2 96-166.3 96C150 448 64 362 64 256S150 64 256 64l0-64C114.6 0 0 114.6 0 256z" />
                <path class="fa-primary" d="M477.7 384c21.8-37.7 34.3-81.4 34.3-128C512 114.6 397.4 0 256 0l0 64c106 0 192 86 192 192c0 35-9.4 67.8-25.7 96l55.4 32z" />
            </svg>
        </div>

        <div class="h-4" />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import {
    FlexRender,
    getCoreRowModel,
    getSortedRowModel,
    useVueTable,
    createColumnHelper,
    columnDef,
    getPaginationRowModel,
    getFilteredRowModel
} from '@tanstack/vue-table'

import { getStockData } from '../services/GetStockData'
let stockData = ref<StockData[]>([])
let errorMsg = ref(null)

onMounted(async () => {
    try {
        const response = await getStockData();
        stockData.value = response.data;
    } catch (error) {
        errorMsg.value = "Failed to fetch stock data.";
    }
});

type StockItem = {
    sku: string,
    name: string,
    colour: string,
    size: string,
    stock: number
}

const stockColumns: ColumnDef<StockItem>[] = [
    {
        accessorKey: "sku",
        header: "Code",
    },
    {
        accessorKey: "name",
        header: "Name",
    },
    {
        accessorKey: "colour",
        header: "Colour",
    },
    {
        accessorKey: "size",
        header: "Size",
    },
    {
        accessorKey: "stock",
        header: "Stock",
        cell: ({ getValue }) => {
            const stock = getValue<number>();
            return stock > 0 ? stock : "Out of Stock";
        },
    },
];

const columnHelper = createColumnHelper<StockItem>()

const data = ref(stockData.value)
const pageSize = ref(20);
const globalFilter = ref('');

// Table Instance with Pagination
const table = useVueTable({
    data: computed(() => stockData.value),
    columns: stockColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(), // activate pagination
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
        pagination: {
            pageSize: pageSize.value,
        },
        sorting: [
            {
                id: 'sku',
                desc: false
            }
        ],
        globalFilter: computed(() => globalFilter.value),
    }
});

const applyGlobalFilter = () => {
    console.log(globalFilter.value)
    console.log(table.getPrePaginationRowModel().rows)
    table.setGlobalFilter(globalFilter.value); // Apply global filter
};

// work out total pages in pagination
const totalPages = computed(() => {
    return Math.ceil(table.getPrePaginationRowModel().rows.length / pageSize.value);
});

const handleSort = (column) => {
    const isSortedAsc = column.column.getIsSorted() === 'asc';
    const newSortDirection = isSortedAsc ? 'desc' : 'asc';
    table.setSorting([{ id: column.id, desc: newSortDirection === 'desc' }]);  // Set sorting state
};

// download helper functions //
const escapeCsvValue = (value: string) => {
    if (typeof value === 'string') {
        // Escape double quotes by doubling them
        value = value.replace(/"/g, '""');

        // If the value contains commas, newlines, or double quotes, enclose it in double quotes
        if (value.includes(',') || value.includes('\n') || value.includes('\r') || value.includes('"')) {
            value = `"${value}"`;
        }
    }
    return value;
};

const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
};

const downloadCSV = (csvContent: string) => {
    const timestamp = getCurrentDateTime();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `CCC-earth-positive-stock-data--${timestamp}.csv`; // Filename with timestamp
    link.click();
};
// download helper functions END //

const downloadAllData = () => {
    const allRows = stockData.value.sort((a, b) => a.sku.localeCompare(b.sku));  // Get all data from the source - unfiltered
    const headers = stockColumns.map(col => col.header);

    let csvContent = headers.map(escapeCsvValue).join(',') + '\n';

    allRows.forEach(row => {
        const rowData = Object.values(row).map(value => escapeCsvValue(value));
        csvContent += rowData.join(',') + '\n';
    });

    downloadCSV(csvContent);
}

const downloadFilteredData = () => {
    const allRows = table.getPrePaginationRowModel().rows; // get filtered values, ignoring pagination
    const headers = stockColumns.map(col => col.header);

    let csvContent = headers.map(escapeCsvValue).join(',') + '\n';

    allRows.forEach(row => {
        const rowData = row.getVisibleCells().map(cell => escapeCsvValue(cell.getValue()));
        csvContent += rowData.join(',') + '\n';
    });

    downloadCSV(csvContent);
}

watch(pageSize, (newSize) => {
    table.setPageSize(newSize); // Dynamically adjust table page size
});

</script>

<style scoped lang="scss">
.spinner {
    position: absolute;
    top: calc(50% - 25px);
    left: calc(50% - 25px);
    width: 50px;
    animation-name: spin;
    animation-duration: 3000ms;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
}

.pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    // gap: 10px;
    border: 1px solid #eee;
    width: fit-content;
    margin: 0 auto;
    margin-top: 10px;
    border-radius: 3px;
    overflow: hidden;

    left: calc(112px / 2);
    position: relative;

    >*:not(:last-child) {
        border-right: 1px solid #eee;
    }

    button {
        border: none;
        width: 30px;
        height: 30px;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: transparent;

        svg {
            height: 12px;
            transition: all 0.2s ease-in-out;
        }

        &:hover,
        &:active,
        &:focus {
            color: white;
            background-color: #019960;

            svg {
                fill: white;
            }
        }
    }

    >span {
        padding: 0 10px;
        height: 30px;
        display: flex;
        align-items: center;
    }
}

button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

table {
    border-top: 1px solid lightgray;
    min-width: 80vw;
    border-collapse: collapse;
}

tbody {
    border-bottom: 1px solid lightgray;
}

tfoot {
    color: gray;
}

tbody {
    tr {
        border-top: 1px solid lightgray;

        &:nth-of-type(odd) {
            background-color: #eee;
        }
    }
}

td,
th {
    padding: 10px 12px;
    text-align: left;
    white-space: nowrap;
}

th {
    cursor: pointer;

    >span {
        display: flex;
        gap: 0.5rem;

        .sorting__arrows {
            width: 15px;
        }
    }

    &:nth-of-type(1) {
        width: 15%;
    }

    &:nth-of-type(2) {}

    &:nth-of-type(3) {
        width: 15%;
    }

    &:nth-of-type(4) {
        width: 10%;
    }

    &:nth-of-type(5) {
        width: 5%;
    }
}

tfoot th {
    font-weight: normal;
}

.logo {
    width: 100px;
    position: relative;
    display: block;
    margin-bottom: 1rem;
}

.table__inputs {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 1rem;
    min-width: 80vw;

    input {
        background: #eee;
        border: none;
        padding: 8px 12px;
    }

    div {
        display: flex;
        gap: 1rem;
    }

    button {
        border: none;
        background: #202427;
        color: white;
        padding: 8px 12px;

        &:hover,
        &:active,
        &:focus {
            background-color: lighten($color: black, $amount: 20%);
        }
    }
}

.table__footer {
    display: flex;
    justify-content: center;

    .page__size {
        margin-top: 10px;
        height: 30px;
        border: none;
        background: #202427;
        color: white;
        padding: 0px 4px;
        border-radius: 3px;
    }
}

@media only screen and (max-width: 700px) {
    table {
        width: 900px;
    }

    .CCC__table {
        overflow-x: scroll;
        width: 100%;
    }

    .pagination {
        margin-left: 0;
        margin-right: 0;
        left: 0;
    }

    .table__footer {
        justify-content: space-between;
    }
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
